// File: Configuration Panel - Scrollable
// Created: 2026-05-12
// Updated: 2026-05-29
// Author: Erick Hernández Silva

import React, { useState, useEffect, useRef } from "react";
import { fetchCar } from "../services/api.js";
import "./VehicleConfigurator.css";

import LayeredPreview from "./carWizard/LayeredPreview";
import StepOptions from "./carWizard/StepOptionsLegacy";
import ContactForm, { type ContactData } from "./carWizard/ContactForm";
import SummaryPanel from "./carWizard/SummaryPanelLegacy";
import ConfiguratorFooter from "./carWizard/ConfiguratorFooter";
import type { CarConfig, Option, Step, SubOption, ViewKey } from "./carWizard/legacyTypes";
import { VIEW_KEYS } from "./carWizard/legacyTypes";
import { IS_PREVIEW } from "../config.js";

interface CarWizardLegacyProps {
  containerId?: string;
  carData?: CarConfig | null;
  embedded?: boolean;
}

const CarWizardLegacy = ({ containerId = "root", carData, embedded = false }: CarWizardLegacyProps) => {
  const [car, setCar] = useState<CarConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const optionsScrollRef = useRef<HTMLDivElement | null>(null);
  const [selectedView, setSelectedView] = useState<ViewKey>("view1");
  const [selectedOptions, setSelectedOptions] = useState<{ [stepIndex: number]: string }>({});
  const [selectedSubOptions, setSelectedSubOptions] = useState<{ [stepIndex: number]: string }>({});
  const [showSummary, setShowSummary] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState<{ [key: string]: boolean }>({});
  const [copySuccess, setCopySuccess] = useState(false);
  const [optionsFading, setOptionsFading] = useState(false);
  const [formData, setFormData] = useState<ContactData>({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [formErrors, setFormErrors] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [step14VariantMode, setStep14VariantMode] = useState<'black' | 'matching'>('matching');

  useEffect(() => {
    const doScroll = () => {
      const el = optionsScrollRef.current;
      if (el) {
        el.scrollTop = 0;
        el.scrollTo({ top: 0, behavior: 'auto' });
      }
      try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch {}
    };
    const r1 = requestAnimationFrame(() => {
      doScroll();
      requestAnimationFrame(doScroll);
    });
    const t = setTimeout(doScroll, 250);
    return () => {
      cancelAnimationFrame(r1);
      clearTimeout(t);
    };
  }, [currentStepIndex, showSummary, showContact]);

  useEffect(() => {
    
    console.log('Can you see me?');
  }, []);

  const normalizeStepNumbers = (steps: Step[]) => {
    const nameToStepNumber: Record<string, number> = {
      'engine': 1,
      'gearbox': 2,
      'exterior paint': 3,
      'front paint': 4,
      'indicators': 5,
      'bull bar + foglights': 6,
      'wheels': 7,
      'parachute hooks': 8,
      'side steps': 9,
      'exhaust pipes': 10,
      'interior color': 11,
      'seating setup': 12,
      'interior package': 13,
      'jerry can': 14,
      'steering wheel': 15,
    };

    return steps.map((s) => {
      const key = (s.stepName || '').trim().toLowerCase();
      const mapped = nameToStepNumber[key];
      if (!mapped) return s;
      return { ...s, stepNumber: mapped };
    });
  };

  type UiStepEntry = {
    title: string;
    stepIndices: number[];
  };

  const getCarIdentifiersFromContainer = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const carIdFromUrl = params.get('carId');
      const createdAtFromUrl = params.get('createdAt');
      if (carIdFromUrl && createdAtFromUrl) {
        return { carId: carIdFromUrl, createdAt: createdAtFromUrl };
      }
    } catch {
      // ignore
    }

    const container = document.getElementById(containerId);
    const carRecord = container?.getAttribute("data-car-record");

    if (!carRecord) {
      return { carId: null, createdAt: null };
    }

    const [carId, createdAt] = carRecord.split("|");
    return { carId, createdAt };
  };

  const findStepIndexByStepNumber = (stepNumber: number) => {
    return car?.steps?.findIndex(s => s.stepNumber === stepNumber) ?? -1;
  };

  const getStep12InteriorColorName = () => {
    const interiorColorIndex = findStepIndexByStepNumber(11);
    if (interiorColorIndex < 0) return null;
    const interiorOpt = getSelectedOption(interiorColorIndex);
    return interiorOpt?.optionName || null;
  };

  const interiorColorIndex = findStepIndexByStepNumber(11);
  const interiorColorOptionId = interiorColorIndex >= 0 ? selectedOptions[interiorColorIndex] : null;
  const interiorPackageIndex = findStepIndexByStepNumber(13);
  const interiorPackageOptionId = interiorPackageIndex >= 0 ? selectedOptions[interiorPackageIndex] : null;

  const getStep13InteriorPackageName = () => {
    const idx = findStepIndexByStepNumber(13);
    if (idx < 0) return null;
    const opt = getSelectedOption(idx);
    return opt?.optionName || null;
  };

  const isMonochromePackage = () => {
    const name = (getStep13InteriorPackageName() || '').toLowerCase();
    // Default to monochrome (match interior) when package not selected yet
    if (!name) return true;
    return name.includes('monochrome');
  };

  const normalizeChoiceName = (value: string) => {
    return (value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  const getContrastSubOption = (
    subOptions: SubOption[] | undefined,
    field: 'contrastSeatingName' | 'contrastSteeringName'
  ): SubOption | null => {
    if (!subOptions || subOptions.length === 0) return null;
    const idx = findStepIndexByStepNumber(13);
    if (idx < 0) return null;
    const pkgOpt = getSelectedOption(idx) as any;
    const targetName = pkgOpt?.[field];
    const norm = normalizeChoiceName(targetName || '');
    if (!norm) return null;
    return subOptions.find(s => normalizeChoiceName(s.optionName || '') === norm) || null;
  };

  // Seating setup (step 12) variant depends on interior color + interior package
  // - Monochrome (or unset) → match interior color, fallback isContrast sub-option
  // - Contrast → isContrast sub-option if flagged, else match interior color
  useEffect(() => {
    if (!car) return;
    const interiorColorName = getStep12InteriorColorName();
    const desiredName = (interiorColorName || '').trim();

    const seatingIndex = findStepIndexByStepNumber(12);
    if (seatingIndex < 0) return;
    const seatingOpt = getSelectedOption(seatingIndex);
    if (!seatingOpt?.subOptions || seatingOpt.subOptions.length === 0) return;

    const subOptions = seatingOpt.subOptions;
    const monochrome = isMonochromePackage();
    const contrastSub = getContrastSubOption(subOptions, 'contrastSeatingName');
    const targetNorm = normalizeChoiceName(desiredName);
    const byTarget = targetNorm
      ? subOptions.find(s => normalizeChoiceName(s.optionName || '') === targetNorm)
      : null;

    const picked = monochrome
      ? (byTarget || contrastSub || subOptions[0])
      : (contrastSub || byTarget || subOptions[0]);

    if (picked && selectedSubOptions[seatingIndex] !== picked.optionId) {
      setSelectedSubOptions(prev => ({
        ...prev,
        [seatingIndex]: picked!.optionId,
      }));
    }
  }, [car, interiorColorOptionId, interiorPackageOptionId]);

  // Steering wheel (step 15) re-pick variant when interior color or package changes (only if Original)
  // - Contrast package → contrast sub-option (from config)
  // - Monochrome (or unset) → match interior color, fallback contrast
  useEffect(() => {
    if (!car) return;
    const interiorColorName = getStep12InteriorColorName();
    const desiredName = (interiorColorName || '').trim();
    const desiredNorm = normalizeChoiceName(desiredName);
    const monochrome = isMonochromePackage();

    const steeringIndex = findStepIndexByStepNumber(15);
    if (steeringIndex < 0) return;
    const steeringStep = car.steps[steeringIndex];
    const originalMainOpt = steeringStep?.options.find(o => (o.optionName || '').trim().toLowerCase() === 'original');
    let steeringOpt = getSelectedOption(steeringIndex);
    let isOriginal = (steeringOpt?.optionName || '').trim().toLowerCase() === 'original';

    // Contrast pkg active + not Original → force switch to Original
    if (!monochrome && !isOriginal && originalMainOpt) {
      setSelectedOptions(prev => ({ ...prev, [steeringIndex]: originalMainOpt.optionId }));
      steeringOpt = originalMainOpt;
      isOriginal = true;
    }

    if (!isOriginal) return;
    const subOptions = steeringOpt?.subOptions;
    if (!subOptions || subOptions.length === 0) return;

    const byDesired = desiredNorm
      ? subOptions.find(s => normalizeChoiceName(s.optionName || '') === desiredNorm)
      : null;
    const contrastSub = getContrastSubOption(subOptions, 'contrastSteeringName');

    let pickedId: string | null = null;
    let pickedMode: 'black' | 'matching' = 'black';
    if (!monochrome && contrastSub) {
      pickedId = contrastSub.optionId;
      pickedMode = 'black';
    } else if (byDesired) {
      pickedId = byDesired.optionId;
      pickedMode = 'matching';
    } else if (contrastSub) {
      pickedId = contrastSub.optionId;
      pickedMode = 'black';
    } else {
      return;
    }

    if (pickedId && selectedSubOptions[steeringIndex] !== pickedId) {
      setSelectedSubOptions(prev => ({
        ...prev,
        [steeringIndex]: pickedId!,
      }));
    }
    console.log('[steering useEffect]', { monochrome, hasContrastSub: !!contrastSub, hasByDesired: !!byDesired, pickedMode, pkgName: getStep13InteriorPackageName(), interiorColorName });
    setStep14VariantMode(pickedMode);
  }, [car, interiorColorOptionId, interiorPackageOptionId]);

  const getStep14State = () => {
    const steeringIndex = findStepIndexByStepNumber(15);
    if (steeringIndex < 0) return { step14Index: -1, step14Opt: null as Option | null };
    const steeringOpt = getSelectedOption(steeringIndex);
    return { step14Index: steeringIndex, step14Opt: steeringOpt };
  };

  const setStep14SubOptionByName = (targetName: string | null) => {
    const { step14Index, step14Opt } = getStep14State();
    if (step14Index < 0 || !step14Opt?.subOptions || step14Opt.subOptions.length === 0) return;
    const desiredNorm = normalizeChoiceName(targetName || '');
    const found = desiredNorm
      ? step14Opt.subOptions.find(s => normalizeChoiceName(s.optionName || '') === desiredNorm)
      : null;

    const contrastSub = getContrastSubOption(step14Opt.subOptions, 'contrastSteeringName');
    const picked = found || contrastSub;
    if (!picked) return;

    setSelectedSubOptions(prev => ({
      ...prev,
      [step14Index]: picked.optionId,
    }));
  };

  // Step 14: if variant is "matching", keep it synced to step 12 interior color
  useEffect(() => {
    if (!car) return;
    const { step14Index, step14Opt } = getStep14State();
    if (step14Index < 0 || !step14Opt) return;

    const isOriginal = step14Opt.optionName?.toLowerCase() === 'original';
    if (!isOriginal) return;
    if (step14VariantMode !== 'matching') return;

    const step12ColorName = getStep12InteriorColorName();
    if (!step12ColorName) return;
    setStep14SubOptionByName(step12ColorName);
  }, [car, selectedOptions, step14VariantMode]);


  useEffect(() => {
    const loadCar = async () => {
      try {
        setLoading(true);
        setError(null);

        let fetched: CarConfig;
        if (carData) {
          fetched = carData;
        } else {
          const { carId, createdAt } = getCarIdentifiersFromContainer();

          if (!carId || !createdAt) {
            throw new Error(
              'Missing or invalid data-car-record on container element. Expected format: "id|createdAt"'
            );
          }

          fetched = await fetchCar(carId, createdAt);
        }

        const normalizedSteps = normalizeStepNumbers(fetched.steps || []);

        // Force seating (12) and steering wheel (15) sub-option names to match
        // interior color (11) option names by position. Data sometimes ships
        // mismatched names ("Bone"/"Saddle" vs "Off white"/"Cognac"), which
        // breaks the by-name matching logic for variant selection.
        const interiorColorStep = normalizedSteps.find(s => s.stepNumber === 11);
        const interiorNames = interiorColorStep?.options?.map(o => o.optionName) || [];
        const syncedSteps = normalizedSteps.map(step => {
          if ((step.stepNumber !== 12 && step.stepNumber !== 15) || interiorNames.length === 0) {
            return step;
          }
          return {
            ...step,
            options: step.options.map(opt => {
              if (!opt.subOptions || opt.subOptions.length === 0) return opt;
              return {
                ...opt,
                subOptions: opt.subOptions.map((sub, i) => {
                  const matchName = interiorNames[i];
                  return matchName ? { ...sub, optionName: matchName } : sub;
                }),
              };
            }),
          };
        });

        const reorderedCarData = {
          ...fetched,
          steps: syncedSteps,
        };
        setCar(reorderedCarData);

        // Initialize with minimal defaults:
        // - Always select Exterior paint (stepNumber 3) so base render is visible
        // - Always select Interior color (stepNumber 11) using the FIRST option
        // - Always select Seating setup (stepNumber 12) using the included-in-base option if available
        // - Select any option explicitly marked isDefault (e.g. wheels)
        const initialOptions: { [stepIndex: number]: string } = {};
        const initialSubOptions: { [stepIndex: number]: string } = {};

        let desiredInteriorName: string | null = null;

        // Resolve interior package default + contrast names up-front
        const pkgStepInit = reorderedCarData.steps?.find((s: Step) => s.stepNumber === 13);
        const pkgDefaultInit = pkgStepInit?.options.find((o: Option) => o.isDefault) || pkgStepInit?.options[0];
        const pkgNameInit = (pkgDefaultInit?.optionName || '').toLowerCase();
        const isContrastDefault = !!pkgDefaultInit && !pkgNameInit.includes('monochrome') && pkgNameInit !== '';
        const contrastSeatingNameInit = (pkgDefaultInit as any)?.contrastSeatingName as string | undefined;
        const contrastSteeringNameInit = (pkgDefaultInit as any)?.contrastSteeringName as string | undefined;

        reorderedCarData.steps?.forEach((step: Step, index: number) => {
          if (step.options && step.options.length > 0) {
            const isExteriorPaint = step.stepNumber === 3;
            const isInteriorColor = step.stepNumber === 11;
            const isSeatingSetup = step.stepNumber === 12;
            const defaultOption = step.options.find(opt => opt.isDefault);

            let chosenOption: Option | undefined;
            if (isExteriorPaint) {
              const baseId = (reorderedCarData as any).baseRenderOptionId as string | undefined;
              chosenOption = (baseId ? step.options.find(opt => opt.optionId === baseId) : undefined) || step.options[0];
            } else if (isInteriorColor) {
              // Always start with the FIRST interior color option
              chosenOption = step.options[0];
              desiredInteriorName = chosenOption?.optionName || null;
            } else if (isSeatingSetup) {
              // Always select a seating option so seats are rendered by default.
              // Prefer the one marked as includedInBaseRender, otherwise fall back to the first.
              chosenOption =
                step.options.find(opt => opt.includedInBaseRender) ||
                step.options[0];
            } else if (defaultOption) {
              chosenOption = defaultOption;
            }

            if (chosenOption) {
              initialOptions[index] = chosenOption.optionId;

              if (step.stepNumber === 12 && chosenOption.subOptions && chosenOption.subOptions.length > 0) {
                // Seating: contrast pkg → contrast sub, else match interior color
                const desiredNorm = normalizeChoiceName(desiredInteriorName || '');
                const byDesired = desiredNorm
                  ? chosenOption.subOptions.find(s => normalizeChoiceName(s.optionName || '') === desiredNorm)
                  : null;
                const contrastNorm = normalizeChoiceName(contrastSeatingNameInit || '');
                const contrastSub = contrastNorm
                  ? chosenOption.subOptions.find(s => normalizeChoiceName(s.optionName || '') === contrastNorm)
                  : null;
                const picked = isContrastDefault
                  ? (contrastSub || byDesired || chosenOption.subOptions[0])
                  : (byDesired || contrastSub || chosenOption.subOptions[0]);
                if (picked) {
                  initialSubOptions[index] = picked.optionId;
                }
              }

              if (step.stepNumber === 15 && chosenOption.subOptions && chosenOption.subOptions.length > 0) {
                // Steering Original: contrast pkg → contrast sub, else match interior color
                const isOriginal = (chosenOption.optionName || '').trim().toLowerCase() === 'original';
                if (isOriginal) {
                  const desiredNorm = normalizeChoiceName(desiredInteriorName || '');
                  const byDesired = desiredNorm
                    ? chosenOption.subOptions.find(s => normalizeChoiceName(s.optionName || '') === desiredNorm)
                    : null;
                  const contrastNorm = normalizeChoiceName(contrastSteeringNameInit || '');
                  const contrastSub = contrastNorm
                    ? chosenOption.subOptions.find(s => normalizeChoiceName(s.optionName || '') === contrastNorm)
                    : null;
                  const picked = isContrastDefault
                    ? (contrastSub || byDesired)
                    : (byDesired || contrastSub);
                  if (picked) {
                    initialSubOptions[index] = picked.optionId;
                  }
                }
              }

              if (step.stepNumber !== 12 && step.stepNumber !== 15 && chosenOption.subOptions && chosenOption.subOptions.length > 0) {
                const defaultSub = chosenOption.subOptions.find(sub => sub.isDefault);
                if (defaultSub) {
                  initialSubOptions[index] = defaultSub.optionId;
                }
              }
            }
          }
        });

        // Check for shared configuration in URL
        const urlParams = new URLSearchParams(window.location.search);
        const configParamFromUrl = urlParams.get('config');
        const configParam = (() => {
          const storageKey = 'sharedConfigParam';
          if (configParamFromUrl) {
            try {
              sessionStorage.setItem(storageKey, configParamFromUrl);
            } catch {
              // ignore
            }
            return configParamFromUrl;
          }
          try {
            return sessionStorage.getItem(storageKey);
          } catch {
            return null;
          }
        })();

        let nextOptions: { [stepIndex: number]: string } = { ...initialOptions };
        let nextSubOptions: { [stepIndex: number]: string } = { ...initialSubOptions };

        if (configParam) {
          try {
            // config sometimes ends up double-encoded depending on how the URL is created.
            // Try single decode first, then a double decode fallback.
            const tryParse = (raw: string) => {
              try {
                return JSON.parse(decodeURIComponent(raw));
              } catch {
                return JSON.parse(decodeURIComponent(decodeURIComponent(raw)));
              }
            };

            const sharedConfig = tryParse(configParam);

            // Merge shared config ON TOP of defaults, so missing keys don't wipe the summary.
            if (sharedConfig?.options) {
              nextOptions = {
                ...nextOptions,
                ...(sharedConfig.options as { [stepIndex: number]: string }),
              };
            }

            if (sharedConfig?.subOptions) {
              nextSubOptions = {
                ...nextSubOptions,
                ...(sharedConfig.subOptions as { [stepIndex: number]: string }),
              };

              // Seating variant is disposition-only; keep our auto-selection.
              const seatingIndex = reorderedCarData.steps?.findIndex((s: Step) => s.stepNumber === 12) ?? -1;
              if (seatingIndex >= 0) {
                delete nextSubOptions[seatingIndex];
              }
            }

            if (sharedConfig?.view) {
              setSelectedView(sharedConfig.view);
            }

            // Go directly to summary
            setShowSummary(true);
            setCurrentStepIndex(reorderedCarData.steps.length - 1);

            // Clean URL after loading configuration
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          } catch (e) {
            console.error('Error parsing shared config:', e);
          }
        }

        setSelectedOptions(nextOptions);
        setSelectedSubOptions(nextSubOptions);
        if (isContrastDefault) setStep14VariantMode('black');

        setLoading(false);
      } catch (err: any) {
        console.error("Configuration load error:", err);
        setError(err.message || "Failed to load car configuration");
        setLoading(false);
      }
    };

    loadCar();
  }, [containerId, carData]);

  const handleOptionChange = (stepIndex: number, optionId: string, applyPreferredView: boolean = true) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [stepIndex]: optionId,
    }));

    if (applyPreferredView && car && !showSummary && !showContact) {
      const step = car.steps[stepIndex];
      const preferred = step?.preferredView;
      if (preferred && preferred !== 'no_change' && preferred !== selectedView) {
        setSelectedView(preferred);
      }
    }

    // Reset sub-option when main option changes
    const step = car?.steps[stepIndex];
    const option = step?.options.find(opt => opt.optionId === optionId);

    // Steering wheel (step 15): if switching away from Original (which has variants), clear any previous variant
    if (step?.stepNumber === 15) {
      const isOriginal = (option?.optionName || '').trim().toLowerCase() === 'original';
      if (!isOriginal) {
        setSelectedSubOptions(prev => {
          if (!prev[stepIndex]) return prev;
          const next = { ...prev };
          delete next[stepIndex];
          return next;
        });
      } else {
        // When switching back to Original, immediately pick a variant.
        // (Can't rely on UI calling setStep14SubOptionByName because state update is async)
        const subOptions = option?.subOptions || [];
        const contrastSub = getContrastSubOption(subOptions, 'contrastSteeringName');
        let picked: typeof subOptions[number] | null = null;
        if (step14VariantMode === 'black') {
          picked = contrastSub || null;
        } else {
          const desiredNorm = normalizeChoiceName(getStep12InteriorColorName() || '');
          picked = desiredNorm
            ? subOptions.find(s => normalizeChoiceName(s.optionName || '') === desiredNorm) || null
            : null;
          if (!picked) picked = contrastSub || null;
        }
        if (picked) {
          setSelectedSubOptions(prev => ({
            ...prev,
            [stepIndex]: picked!.optionId,
          }));
        }
      }
    }

    // Interior package (stepNumber 13): sync steering wheel + seating immediately
    if (step?.stepNumber === 15) {
      // already handled above
    }
    if (step?.stepNumber === 13) {
      const pkgName = (option?.optionName || '').toLowerCase();
      const willBeMonochrome = pkgName.includes('monochrome') || !pkgName;
      const steeringIdx = findStepIndexByStepNumber(15);
      if (steeringIdx >= 0 && car) {
        const steeringStep = car.steps[steeringIdx];
        const originalMain = steeringStep?.options.find(o => (o.optionName || '').trim().toLowerCase() === 'original');
        const currentSteering = getSelectedOption(steeringIdx);
        const currentIsOriginal = (currentSteering?.optionName || '').trim().toLowerCase() === 'original';
        const useOpt = currentIsOriginal ? currentSteering : (!willBeMonochrome ? originalMain : currentSteering);
        if (!willBeMonochrome && !currentIsOriginal && originalMain) {
          setSelectedOptions(prev => ({ ...prev, [steeringIdx]: originalMain.optionId }));
        }
        const steerSubs = useOpt?.subOptions || [];
        const contrastName = (option as any)?.contrastSteeringName as string | undefined;
        const contrastNorm = normalizeChoiceName(contrastName || '');
        const contrastSub = contrastNorm
          ? steerSubs.find(s => normalizeChoiceName(s.optionName || '') === contrastNorm)
          : null;
        const colorName = getStep12InteriorColorName() || '';
        const colorNorm = normalizeChoiceName(colorName);
        const byColor = colorNorm
          ? steerSubs.find(s => normalizeChoiceName(s.optionName || '') === colorNorm)
          : null;
        let picked: typeof steerSubs[number] | null = null;
        let mode: 'black' | 'matching' = 'matching';
        if (!willBeMonochrome && contrastSub) {
          picked = contrastSub;
          mode = 'black';
        } else if (byColor) {
          picked = byColor;
          mode = 'matching';
        } else if (contrastSub) {
          picked = contrastSub;
          mode = 'black';
        }
        if (picked) {
          setSelectedSubOptions(prev => ({ ...prev, [steeringIdx]: picked!.optionId }));
        }
        setStep14VariantMode(mode);
      }
      // Seating sync also (uses same logic as useEffect)
      const seatingIdx = findStepIndexByStepNumber(12);
      if (seatingIdx >= 0 && car) {
        const seatingOpt = getSelectedOption(seatingIdx);
        const seatSubs = seatingOpt?.subOptions || [];
        const contrastName = (option as any)?.contrastSeatingName as string | undefined;
        const contrastNorm = normalizeChoiceName(contrastName || '');
        const contrastSub = contrastNorm
          ? seatSubs.find(s => normalizeChoiceName(s.optionName || '') === contrastNorm)
          : null;
        const colorName = getStep12InteriorColorName() || '';
        const colorNorm = normalizeChoiceName(colorName);
        const byColor = colorNorm
          ? seatSubs.find(s => normalizeChoiceName(s.optionName || '') === colorNorm)
          : null;
        const picked = willBeMonochrome
          ? (byColor || contrastSub || seatSubs[0])
          : (contrastSub || byColor || seatSubs[0]);
        if (picked) {
          setSelectedSubOptions(prev => ({ ...prev, [seatingIdx]: picked.optionId }));
        }
      }
      return;
    }

    // Seating setup (stepNumber 12): auto-select variant per interior package, UI selector hidden
    if (step?.stepNumber === 12) {
      const interiorColorName = getStep12InteriorColorName();
      const monochrome = isMonochromePackage();
      const subOptions = option?.subOptions || [];
      const contrastSub = subOptions.find(s => (s as any).isContrast === true);

      const targetNorm = normalizeChoiceName(interiorColorName || '');
      const byTarget = targetNorm
        ? subOptions.find(s => normalizeChoiceName(s.optionName || '') === targetNorm)
        : null;
      const picked = monochrome
        ? (byTarget || contrastSub || subOptions[0] || null)
        : (contrastSub || byTarget || subOptions[0] || null);

      if (picked) {
        setSelectedSubOptions(prev => ({
          ...prev,
          [stepIndex]: picked!.optionId,
        }));
      }
      return;
    }

    if (option?.subOptions && option.subOptions.length > 0) {
      const defaultSub = option.subOptions.find(sub => sub.isDefault);
      if (defaultSub) {
        setSelectedSubOptions((prev) => ({
          ...prev,
          [stepIndex]: defaultSub.optionId,
        }));
      }
    } else {
      // Clear sub-option if no sub-options for this option
      setSelectedSubOptions((prev) => {
        const next = { ...prev };
        delete next[stepIndex];
        return next;
      });
    }
    
  };

  const handleSubOptionChange = (stepIndex: number, subOptionId: string) => {
    setSelectedSubOptions((prev) => ({
      ...prev,
      [stepIndex]: subOptionId,
    }));
  };



  // Preload ALL images from ALL steps (not just current step)
  useEffect(() => {
    if (!car) return;

    car.steps.forEach((step, stepIndex) => {
      // Preload all option images
      step.options.forEach(option => {
        VIEW_KEYS.forEach(viewKey => {
          const imageUrl = option.images[viewKey];
          if (imageUrl && imageUrl.startsWith('http')) {
            const img = new Image();
            img.src = imageUrl;
          }
        });

        // Preload sub-option images if they exist
        if (step.stepNumber === 12) {
          // Seating: only preload the selected variant to avoid loading overlapping seat colors
          const selectedSubId = selectedSubOptions[stepIndex];
          const selectedSub = selectedSubId
            ? option.subOptions?.find(s => s.optionId === selectedSubId)
            : null;

          if (selectedSub) {
            VIEW_KEYS.forEach(viewKey => {
              const imageUrl = selectedSub.images[viewKey];
              if (imageUrl && imageUrl.startsWith('http')) {
                const img = new Image();
                img.src = imageUrl;
              }
            });
          }
          return;
        }

        option.subOptions?.forEach(subOption => {
          VIEW_KEYS.forEach(viewKey => {
            const imageUrl = subOption.images[viewKey];
            if (imageUrl && imageUrl.startsWith('http')) {
              const img = new Image();
              img.src = imageUrl;
            }
          });
        });
      });
    });
  }, [car, selectedSubOptions]);

  const getSelectedOption = (stepIndex: number): Option | null => {
    const step = car?.steps[stepIndex];
    if (!step) return null;
    
    const optionId = selectedOptions[stepIndex];
    return step.options.find(opt => opt.optionId === optionId) || null;
  };

  const getSelectedSubOption = (stepIndex: number): SubOption | null => {
    const option = getSelectedOption(stepIndex);
    if (!option?.subOptions) return null;
    
    const subOptionId = selectedSubOptions[stepIndex];
    return option.subOptions.find(sub => sub.optionId === subOptionId) || null;
  };

  const getUiStepEntries = () => {
    if (!car) return [] as UiStepEntry[];

    const idx = (stepNumber: number) => findStepIndexByStepNumber(stepNumber);
    const pick = (stepNumbers: number[]) => stepNumbers.map(idx).filter(i => i >= 0);

    const entries: UiStepEntry[] = [
      { title: 'Powertrain', stepIndices: pick([1, 2]) },
      { title: 'Exterior Color', stepIndices: pick([3]) },
      { title: 'Equipment', stepIndices: pick([6, 4, 5, 8, 14]) },
      { title: 'Stance', stepIndices: pick([7, 9, 10]) },
      { title: 'Upholstry', stepIndices: pick([11]) },
      { title: 'Seating', stepIndices: pick([12]) },
      { title: 'Dash', stepIndices: pick([13, 15]) },
    ].filter(e => e.stepIndices.length > 0);

    return entries;
  };

  const getUiStepPosition = () => {
    const entries = getUiStepEntries();
    const pos = entries.findIndex(e => e.stepIndices.includes(currentStepIndex));
    return { entries, pos: pos < 0 ? 0 : pos };
  };

  const calculateTotalPrice = () => {
    if (!car) return 0;
    let total = car.basePrice || 0;
    const baseRenderId = (car as any).baseRenderOptionId as string | undefined;

    car.steps?.forEach((_, index) => {
      const option = getSelectedOption(index);
      if (option) {
        const isBaseRender = option.includedInBaseRender || option.optionId === baseRenderId;
        if (!isBaseRender) {
          total += option.optionPrice || 0;
        }

        // Add sub-option price if exists (Seating + Steering variants are disposition-only)
        const step = car.steps[index];
        const skipSub = step?.stepNumber === 12 || step?.stepNumber === 15;
        const subOption = skipSub ? null : getSelectedSubOption(index);
        if (subOption) {
          total += subOption.optionPrice || 0;
        }
      }
    });

    return total;
  };

  const getImageLayers = (viewKey: ViewKey = selectedView) => {
    if (!car) return [];
    
    const layers: { url: string; type: string; zIndex: number }[] = [];

    // Always include step 3 (Exterior paint/body) as base layer if available
    const step3Index = findStepIndexByStepNumber(3);
    const step3Option = step3Index >= 0 ? getSelectedOption(step3Index) : null;
    if (step3Option && step3Index >= 0) {
      const step3SubOption = getSelectedSubOption(step3Index);
      const step3ImageSource = step3SubOption || step3Option;
      const step3ImageUrl = step3ImageSource.images[viewKey];
      
      if (step3ImageUrl && step3ImageUrl.startsWith('http')) {
        layers.push({ 
          url: step3ImageUrl, 
          type: `step-3-base-${step3Option.optionId}`,
          zIndex: 3,
        });
      }
    }

    car.steps?.forEach((step, stepIndex) => {
      // Skip step 3 since we already added it as base
      if (step3Index >= 0 && stepIndex === step3Index) return;
      
      const option = getSelectedOption(stepIndex);
      if (!option) return;

      // Use sub-option images if available, otherwise use main option images
      const subOption = getSelectedSubOption(stepIndex);
      const imageSource = (() => {
        if (step.stepNumber === 12 && subOption) {
          const subUrl = subOption.images?.[viewKey];
          if (subUrl && subUrl.startsWith('http')) return subOption;

          // If the selected seating variant has no image for this view, try to find
          // another variant that does, so seats still appear in views like view4.
          const fallbackSub = option.subOptions?.find(s => {
            const u = s.images?.[viewKey];
            return !!u && u.startsWith('http');
          });
          if (fallbackSub) return fallbackSub;

          return option;
        }
        if (step.stepNumber === 15 && subOption) {
          const subUrl = subOption.images?.[viewKey];
          if (subUrl && subUrl.startsWith('http')) return subOption;

          // Steering variant has no image for this view → fall back to the
          // variant matching the interior color, then any variant with image.
          const interiorName = getStep12InteriorColorName();
          const interiorNorm = normalizeChoiceName(interiorName || '');
          const byInterior = interiorNorm
            ? option.subOptions?.find(s => {
                if (normalizeChoiceName(s.optionName || '') !== interiorNorm) return false;
                const u = s.images?.[viewKey];
                return !!u && u.startsWith('http');
              })
            : null;
          if (byInterior) return byInterior;

          const fallbackSub = option.subOptions?.find(s => {
            const u = s.images?.[viewKey];
            return !!u && u.startsWith('http');
          });
          if (fallbackSub) return fallbackSub;

          return option;
        }
        return subOption || option;
      })();

      const imageUrl = imageSource.images[viewKey];

      // Only add layer if image exists and is not a placeholder
      if (imageUrl && imageUrl.startsWith('http')) {
        // Exhaust pipes (step 10) should render below side steps (step 9)
        let zIndex = step.stepNumber;
        if (step.stepNumber === 10) {
          zIndex = 8;
        }

        layers.push({
          url: imageUrl,
          type: `step-${stepIndex}-${option.optionId}`,
          zIndex,
        });
      }
    });

    layers.sort((a, b) => a.zIndex - b.zIndex);
    return layers;
  };

  const allViewLayers = (() => {
    const map = {} as Record<ViewKey, { url: string; type: string; zIndex: number }[]>;
    VIEW_KEYS.forEach((vk) => {
      map[vk] = getImageLayers(vk);
    });
    return map;
  })();

  const goToNextStep = () => {
    setOptionsFading(true);
    if (optionsScrollRef.current) optionsScrollRef.current.scrollTop = 0;
    setTimeout(() => {
      if (optionsScrollRef.current) optionsScrollRef.current.scrollTop = 0;
      if (car) {
        const { entries, pos } = getUiStepPosition();
        const next = entries[pos + 1];
        if (next) {
          setCurrentStepIndex(next.stepIndices[0]);
        } else {
          setShowSummary(true);
        }
      }
      setOptionsFading(false);
    }, 150);
  };

  const goToPreviousStep = () => {
    if (showContact) {
      setShowContact(false);
      setShowSummary(true);
    } else if (showSummary) {
      setShowSummary(false);
      const entries = getUiStepEntries();
      const last = entries[entries.length - 1];
      setCurrentStepIndex(last ? last.stepIndices[0] : (car!.steps.length - 1));
    } else if (currentStepIndex > 0) {
      setOptionsFading(true);
      if (optionsScrollRef.current) optionsScrollRef.current.scrollTop = 0;
      setTimeout(() => {
        if (optionsScrollRef.current) optionsScrollRef.current.scrollTop = 0;
        if (car) {
          const { entries, pos } = getUiStepPosition();
          const prev = entries[pos - 1];
          if (prev) {
            setCurrentStepIndex(prev.stepIndices[0]);
          }
        }
        setOptionsFading(false);
      }, 150);
    }
  };

  // Hardcoded rule: if Side steps = "Automatic electric", force Exhaust pipes to "Hidden"
  useEffect(() => {
    if (!car) return;

    const sideStepsIndex = findStepIndexByStepNumber(9);
    const exhaustIndex = findStepIndexByStepNumber(10);
    if (sideStepsIndex < 0 || exhaustIndex < 0) return;

    const sideOpt = getSelectedOption(sideStepsIndex);
    const isAutoElectric = (sideOpt?.optionName || '').trim().toLowerCase() === 'automatic electric';
    if (!isAutoElectric) return;

    const exhaustStep = car.steps[exhaustIndex];
    const hidden = exhaustStep?.options?.find(o => (o.optionName || '').trim().toLowerCase() === 'hidden');
    if (!hidden) return;

    if (selectedOptions[exhaustIndex] === hidden.optionId) return;
    handleOptionChange(exhaustIndex, hidden.optionId, false);
  }, [car, selectedOptions]);

  const validateForm = () => {
    const errors = { firstName: '', lastName: '', email: '', phone: '' };
    let isValid = true;

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
      isValid = false;
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
      isValid = false;
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('[Zapier] handleFormSubmit');
    
    if (!validateForm()) {
      console.log('[Zapier] validation failed');
      return;
    }

    // Generate shareable link (same format as Save & Share)
    const baseUrl = window.location.origin + window.location.pathname;
    const params = new URLSearchParams();

    if (car?.id) params.append('carId', car.id);
    if (car?.createdAt) params.append('createdAt', car.createdAt);

    const configJson = JSON.stringify({
      options: selectedOptions,
      subOptions: selectedSubOptions,
      view: selectedView,
    });
    params.append('config', encodeURIComponent(configJson));

    const shareableLink = `${baseUrl}?${params.toString()}`;

    const selectionsText = (car?.steps || [])
      .map((step, stepIndex) => {
        const option = getSelectedOption(stepIndex);
        const subOption = getSelectedSubOption(stepIndex);
        if (!option) return null;
        const stepName = (step?.stepName || `Step ${stepIndex + 1}`).trim();
        const optionName = (option.optionName || '').trim();
        const subName = (subOption?.optionName || '').trim();
        const choice = subName ? `${optionName} - ${subName}` : optionName;
        return `${stepName}: ${choice}`;
      })
      .filter(Boolean)
      .join('\n');

    const formattedPrice = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(totalPrice) || 0);

    const leadsBodyText = [
      `Name: ${formData.firstName} ${formData.lastName}`.trim(),
      `Email: ${formData.email}`,
      `Phone: ${formData.phone}`,
      `Car: ${car?.carName || ''}`.trim(),
      `Total Price: ${formattedPrice}`,
      `Configuration Link: ${shareableLink}`,
      ...((formData.message || '').trim() ? ['', 'Message:', formData.message.trim()] : []),
      '',
      'Selections:',
      selectionsText,
    ]
      .filter((line) => line !== undefined && line !== null)
      .join('\n');

    const leadsHtml = `<pre style="font-family:Arial,sans-serif;font-size:14px;white-space:pre-wrap;">${leadsBodyText
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')}</pre>`;

    const leadsJsonBody = JSON.stringify({
      type: 'leads',
      to: 'leads@touringdesignco.com',
      from: 'Touring Configurator <noreply@touringdesignco.com>',
      replyTo: (formData.email || '').trim(),
      // Plain fields for the Zap (sheet columns, CRM).
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      email: (formData.email || '').trim(),
      phone: (formData.phone || '').trim(),
      message: (formData.message || '').trim(),
      subject: `New build submission - ${formData.firstName} ${formData.lastName}`.trim(),
      text: leadsBodyText,
      html: leadsHtml,
    });

    const customerName = `${formData.firstName} ${formData.lastName}`.trim();
    const customerBodyText = [
      `Hi ${formData.firstName || 'there'},`,
      '',
      'Your spec is below. We’ve held it in our system, so when you’re ready to move forward - or want to make a change - it’ll be exactly where you left it.',
      '',
      'Your build',
      selectionsText,
      '',
      'Quoted at',
      formattedPrice,
      '',
      'When you’re ready to discuss this build - refining the spec, walking through the timeline, or talking through where you’ll take it first - our team is here.',
      '',
      'Reach out anytime: ryan@touringdesignco.com',
      '',
      '— Ryan',
      'Touring',
    ].join('\n');

    const escapeHtml = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const selectionsHtml = selectionsText
      .split('\n')
      .map((line) => `<li>${escapeHtml(line)}</li>`)
      .join('');

    const customerHtml = `
<div style="font-family:Arial,sans-serif;font-size:14px;color:#111;line-height:1.5;max-width:600px;">
  <img src="https://touring-configurator-prod.s3.us-east-2.amazonaws.com/email+newsletter.jpg" alt="Touring" style="display:block;width:100%;max-width:600px;height:auto;margin:0 0 16px 0;" />
  <p>Hi ${escapeHtml(formData.firstName || 'there')},</p>
  <p>Your spec is below. We&rsquo;ve held it in our system, so when you&rsquo;re ready to move forward - or want to make a change - it&rsquo;ll be exactly where you left it.</p>
  <h3 style="margin-bottom:4px;">Your build</h3>
  <ul style="padding-left:18px;margin-top:0;">${selectionsHtml}</ul>
  <p><strong>Quoted at</strong><br/>${escapeHtml(formattedPrice)}</p>
  <p>When you&rsquo;re ready to discuss this build - refining the spec, walking through the timeline, or talking through where you&rsquo;ll take it first - our team is here.</p>
  <p>Reach out anytime: <a href="mailto:ryan@touringdesignco.com">ryan@touringdesignco.com</a></p>
  <p>&mdash; Ryan<br/>Touring</p>
</div>`.trim();

    const customerJsonBody = JSON.stringify({
      type: 'customer',
      to: (formData.email || '').trim(),
      from: 'Ryan at Touring <ryan@touringdesignco.com>',
      replyTo: 'ryan@touringdesignco.com',
      subject: 'Your build, saved.',
      name: customerName,
      email: (formData.email || '').trim(),
      phone: (formData.phone || '').trim(),
      message: (formData.message || '').trim(),
      configurationLink: shareableLink,
      totalPrice: String(totalPrice),
      selectionsText,
      text: customerBodyText,
      html: customerHtml,
    });

    // Prepare data to send to backend
    const submissionData = {
      // Contact information
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      
      // Configuration link for TouringDesign team
      configurationLink: shareableLink,

      // Human-readable selections summary (for Zapier)
      selectionsText,
      
      // Raw configuration data
      carId: car?.id,
      carName: car?.carName,
      selectedOptions,
      selectedSubOptions,
      totalPrice: totalPrice,
      
      // Timestamp
      submittedAt: new Date().toISOString()
    };

    const zapierWebhookUrl = 'https://hooks.zapier.com/hooks/catch/12922324/upcaksb/';
    const disableRedirectForDebug = false;
    const forceFetchForDebug = false;

    const sendZapier = async (jsonBody: string, label: string) => {
      if (IS_PREVIEW) {
        console.info(`[preview] Zapier ${label} webhook not sent`, JSON.parse(jsonBody));
        return;
      }
      try {
        let sent = false;
        if (!forceFetchForDebug) {
          try {
            const blob = new Blob([jsonBody], { type: 'text/plain' });
            sent = navigator.sendBeacon?.(zapierWebhookUrl, blob) ?? false;
          } catch {
            sent = false;
          }
        }

        if (!sent) {
          console.log(`[Zapier:${label}] sending via fetch`);
          await fetch(zapierWebhookUrl, {
            method: 'POST',
            body: jsonBody,
            keepalive: true,
            mode: 'no-cors',
          });
          console.log(`[Zapier:${label}] fetch done (opaque response expected)`);
        }
      } catch (error) {
        console.error(`Error sending Zapier webhook (${label}):`, error);
      }
    };

    await Promise.all([
      sendZapier(leadsJsonBody, 'leads'),
      sendZapier(customerJsonBody, 'customer'),
    ]);

    console.log('Form submitted with configuration:', submissionData);

    // Redirect to external thank-you page with config as query params
    const thankYouParams = new URLSearchParams();

    Object.entries(selectedOptions).forEach(([stepIndex, optionId]) => {
      thankYouParams.append(`step${stepIndex}`, optionId);
    });

    Object.entries(selectedSubOptions).forEach(([stepIndex, subOptionId]) => {
      thankYouParams.append(`sub${stepIndex}`, subOptionId);
    });

    if (car?.id) thankYouParams.append('carId', car.id);
    if (car?.createdAt) thankYouParams.append('createdAt', car.createdAt);
    thankYouParams.append('totalPrice', String(totalPrice));

    if (IS_PREVIEW) {
      window.alert('Preview: the form works, but no lead or email was sent and there is no thank-you page here.');
    } else if (!disableRedirectForDebug) {
      window.location.assign(`/thank-you?${thankYouParams.toString()}`);
    }
  };

  const handleSaveSummary = async () => {
    try {
      // Build configuration URL with all selected options
      const baseUrl = window.location.origin + window.location.pathname;
      const params = new URLSearchParams();
      
      // Add car ID and timestamp
      if (car?.id && car?.createdAt) {
        params.append('carId', car.id);
        params.append('createdAt', car.createdAt);
      }
      
      // Add selected options as JSON
      const configJson = JSON.stringify({
        options: selectedOptions,
        subOptions: selectedSubOptions,
        view: selectedView,
      });
      params.append('config', encodeURIComponent(configJson));
      
      const fullUrl = `${baseUrl}?${params.toString()}`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(fullUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Error copying configuration URL. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${embedded ? 'h-64' : 'min-h-screen'}`}>
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-gray-600">Loading car configuration...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${embedded ? 'h-64' : 'min-h-screen'}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!car) {
    return null;
  }

  const currentStep = car.steps[currentStepIndex];
  const selectedOption = getSelectedOption(currentStepIndex);
  const selectedSubOption = getSelectedSubOption(currentStepIndex);

  const { entries: uiEntries, pos: uiPos } = getUiStepPosition();
  const uiTotal = uiEntries.length || car.steps.length;

  const activeUiEntry = uiEntries[uiPos];
  const activeStepIndices = activeUiEntry?.stepIndices || [currentStepIndex];

  const currentImageSource = selectedSubOption || selectedOption;

  const isUiGroupComplete = activeStepIndices.every((stepIndex) => {
    const opt = getSelectedOption(stepIndex);
    if (!opt) return false;
    const step = car.steps[stepIndex];
    if (step?.stepNumber !== 12 && opt.subOptions && opt.subOptions.length > 0) {
      const sub = getSelectedSubOption(stepIndex);
      if (!sub) return false;
    }
    return true;
  });
  
  // Generate thumbnail for a specific view with all current layers
  const getThumbnailForView = (viewKey: ViewKey) => {
    return getImageLayers(viewKey);
  };
  const totalPrice = calculateTotalPrice();

  return (
    <div className={`${embedded ? 'h-full min-h-[700px]' : 'h-screen'} flex flex-col bg-white`}>
      <div className={`grid ${embedded ? 'grid-rows-[300px_1fr]' : 'grid-rows-[minmax(280px,42vh)_1fr] lg:flex lg:flex-row'} flex-1 gap-0 overflow-hidden min-h-0`}>
        <LayeredPreview
          carName={car.carName}
          selectedView={selectedView}
          viewLoading={viewLoading}
          allViewLayers={allViewLayers}
          imagesLoaded={imagesLoaded}
          setImagesLoaded={setImagesLoaded}
          viewKeys={VIEW_KEYS}
          setSelectedView={setSelectedView}
          setViewLoading={setViewLoading}
          stepLabel={`Step ${uiPos + 1} of ${uiTotal}`}
          stepTitle={activeUiEntry?.title || currentStep.stepName}
          embedded={embedded}
        />

        {/* Configuration Panel - Scrollable */}
        <div className={`w-full ${embedded ? '' : 'lg:w-96 lg:min-w-[380px]'} flex flex-col bg-white overflow-hidden order-2 lg:order-2 min-h-0`}>
          {!showSummary ? (
            <>
              {/* Step Header - Fixed */}
              <div className={`p-3 pb-0 md:p-6 md:pb-0 ${embedded ? 'hidden' : 'hidden lg:block'}`}>
                <div className="mb-2 md:mb-4">
                  <div className="text-sm text-gray-500 mb-1 md:mb-2">
                    Step {uiPos + 1} of {uiTotal}
                  </div>
                  <h2 className="text-sm font-semibold text-gray-800 mb-0.5 md:mb-1 uppercase">
                    {activeUiEntry?.title || currentStep.stepName}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {activeUiEntry
                      ? activeUiEntry.stepIndices
                          .map(i => car.steps[i]?.stepName)
                          .filter(Boolean)
                          .join(' • ')
                      : currentStep.stepDescription}
                  </p>
                  {currentStep.isTextOnly && (
                    <div className="hidden px-3 py-1 bg-yellow-100 text-yellow-800 text-xs rounded ">
                      Text Only - No visual preview
                    </div>
                  )}
                </div>
              </div>

              {/* Scrollable Options Area */}
              <div ref={optionsScrollRef} className={`flex-1 min-h-0 overflow-y-auto px-4 md:px-6 pt-3 lg:pt-0 transition-opacity duration-300 ${optionsFading ? 'opacity-0' : 'opacity-100'}`}>
                {(() => {
                  const sideStepsIndex = findStepIndexByStepNumber(9);
                  const sideOpt = sideStepsIndex >= 0 ? getSelectedOption(sideStepsIndex) : null;
                  const isAutoElectric = (sideOpt?.optionName || '').trim().toLowerCase() === 'automatic electric';

                  return (
                    <>
                      {activeStepIndices.map((stepIndex) => {
                        const step = car.steps[stepIndex];
                        const stepSelectedOption = getSelectedOption(stepIndex);
                        const isExhaust = step.stepNumber === 10;

                        return (
                          <StepOptions
                            key={`${step.stepNumber}-${stepIndex}`}
                            currentStep={step}
                            currentStepIndex={stepIndex}
                            selectedOption={stepSelectedOption}
                            selectedOptions={selectedOptions}
                            selectedSubOptions={selectedSubOptions}
                            step14VariantMode={step14VariantMode}
                            handleOptionChange={handleOptionChange}
                            handleSubOptionChange={handleSubOptionChange}
                            setStep14VariantMode={setStep14VariantMode}
                            setStep14SubOptionByName={setStep14SubOptionByName}
                            getStep12InteriorColorName={getStep12InteriorColorName}
                            contrastSteeringName={(() => {
                              const idx = findStepIndexByStepNumber(13);
                              if (idx < 0) return null;
                              const pkgStep = car?.steps?.[idx];
                              const opt = pkgStep?.options.find(o => (o as any).contrastSteeringName);
                              return (opt as any)?.contrastSteeringName || null;
                            })()}
                            headerText={(step.stepName || 'SELECT OPTION').toUpperCase()}
                            disabled={isExhaust ? isAutoElectric : false}
                          />
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </>
          ) : showContact ? (
            <ContactForm
              formData={formData}
              formErrors={formErrors}
              setFormData={setFormData}
              handleFormSubmit={handleFormSubmit}
            />
          ) : (
            <SummaryPanel
              car={car}
              getSelectedOption={getSelectedOption}
              getSelectedSubOption={getSelectedSubOption}
              selectedOptions={selectedOptions}
              selectedSubOptions={selectedSubOptions}
            />
          )}

          {/* Bottom: Navigation & Views - Always at bottom */}
          <ConfiguratorFooter
            showSummary={showSummary}
            showContact={showContact}
            currentStepIndex={currentStepIndex}
            totalSteps={uiTotal}
            isLastUiStep={(() => {
              if (!car) return false;
              const { entries, pos } = getUiStepPosition();
              return pos === entries.length - 1;
            })()}
            isNextDisabled={!isUiGroupComplete}
            viewLoading={viewLoading}
            selectedView={selectedView}
            setSelectedView={setSelectedView}
            setViewLoading={setViewLoading}
            currentImageSource={currentImageSource}
            getThumbnailForView={getThumbnailForView}
            copySuccess={copySuccess}
            handleSaveSummary={handleSaveSummary}
            goToPreviousStep={goToPreviousStep}
            goToNextStep={goToNextStep}
            onContact={() => setShowContact(true)}
            totalPrice={totalPrice}
            embedded={embedded}
          />
        </div>
      </div>
    </div>
  );
};

export default CarWizardLegacy;
